import { Button } from "@/components/ui/button";
import { useT } from "@/i18n";
import { getFormatLocale } from "@/i18n/runtime";
import { LiveDuration } from "@/lib/LiveDuration";
import { episodeRailClass } from "@/lib/severity";

import { StatusBadges } from "./badges";
import { MailToggle } from "./MailToggle";
import type { ServiceTile } from "./serviceOverview";
import { Sparkline } from "./Sparkline";
import { clock, windowStamp } from "./stamps";

/**
 * One Service on the board. It never becomes an Episode row: at most one open Episode is quoted
 * (severity rail, meta line, first log body) with nothing to act on — no Acknowledge, no Solve.
 * The moment a reader wants to act, the Episodes page owns that; the buttons hand off to Logs,
 * Traces and Episodes with the service already applied — the card itself is not a click target.
 */
export function ServiceCard(props: {
  tile: ServiceTile;
  showVolume: boolean;
  subscribed: boolean;
  viaApplication: boolean;
  onToggleMail: () => void;
  onLogs: () => void;
  onTraces: () => void;
  onEpisodes: () => void;
}) {
  const t = useT();
  const { tile } = props;
  const open = tile.episodes !== undefined && tile.episodes.open > 0;
  const quietedOnly = !open && tile.episodes !== undefined && tile.episodes.quieted > 0;
  const quoted = open ? tile.episodes?.latest : null;
  const logs =
    tile.volume === undefined
      ? undefined
      : tile.volume.error + tile.volume.warn + tile.volume.info + tile.volume.debug;

  return (
    <div
      data-testid="service-card"
      className="relative flex flex-col rounded-[11px] border border-[#1E344C] bg-card p-3.5"
    >
      {/* An overlay ring instead of a changed border, so the layout never shifts. */}
      {open && (
        <div className="pointer-events-none absolute inset-[-1px] rounded-xl border border-[rgba(229,84,74,0.34)] shadow-[inset_3px_0_0_#E5544A]" />
      )}
      {/* Quieted trouble gets the same left rail in the warn tone — settled, but not clear. */}
      {quietedOnly && (
        <div className="pointer-events-none absolute inset-[-1px] rounded-xl border border-[rgba(233,164,60,0.32)] shadow-[inset_3px_0_0_#C97B12]" />
      )}

      <div className="flex items-center gap-[9px]">
        <span className="truncate font-mono text-sm font-medium">{tile.label}</span>
        <span className="ml-auto">
          <MailToggle
            subscribed={props.subscribed}
            viaApplication={props.viaApplication}
            applicationName={tile.applicationName}
            onToggle={props.onToggleMail}
          />
        </span>
      </div>

      <div className="mt-2.5 flex items-center gap-[9px]">
        <StatusBadges tile={tile} />
      </div>

      <div className="mt-[13px] grid grid-cols-3 gap-2">
        <Count caption={t.overview.card.caption.errors} value={tile.volume?.error} tone="error" />
        <Count caption={t.overview.card.caption.warn} value={tile.volume?.warn} tone="warn" />
        <Count caption={t.overview.card.caption.logs} value={logs} tone="neutral" />
      </div>

      {props.showVolume && (
        <>
          <div className="mt-[13px]">
            <Sparkline buckets={tile.volume?.buckets} />
          </div>
          <div className="mt-1 flex justify-between font-mono text-[9.5px] text-[#5F7590]">
            <span>
              {tile.volume?.buckets[0] === undefined ? "" : windowStamp(tile.volume.buckets[0].start)}
            </span>
            <span>{t.overview.card.now}</span>
          </div>
        </>
      )}

      {quoted != null && (
        <div className="mt-3 rounded-lg border border-[rgba(229,84,74,0.2)] bg-[rgba(229,84,74,0.07)] px-[11px] py-[9px]">
          <div className="flex items-center gap-2">
            <span
              className={`h-[15px] w-[3px] shrink-0 rounded-[2px] ${episodeRailClass(quoted.firstMatchSeverity)}`}
            />
            <span className="truncate font-mono text-[10.5px] text-[#A9BDD1]">
              {t.overview.card.opened(clock(quoted.openedAt))} · <LiveDuration since={quoted.openedAt} />
              {quoted.watch === "HealthCheck" ? (
                // Nothing was logged, so there is nothing to count.
                ` · ${t.overview.card.healthCheck}`
              ) : (
                <>
                  {" · "}
                  {t.overview.card.errCount(Number(quoted.errorCount))}
                  {Number(quoted.warnCount) > 0
                    && ` · ${t.overview.card.warnCount(Number(quoted.warnCount))}`}
                </>
              )}
            </span>
          </div>
          {quoted.firstMatchDetail != null && (
            <div className="mt-1.5 truncate font-mono text-xs text-[#DCE8F3]">{quoted.firstMatchDetail}</div>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={props.onLogs}>
          {t.overview.actions.toLogs}
        </Button>
        <Button variant="ghost" size="sm" onClick={props.onTraces}>
          {t.overview.actions.toTraces}
        </Button>
        <Button variant="ghost" size="sm" onClick={props.onEpisodes}>
          {t.overview.actions.toEpisodes}
        </Button>
      </div>
    </div>
  );
}

/** A zero renders grey in every band: a grey zero is the point of the tile, a red 0 is a lie. */
function Count(props: { caption: string; value: number | undefined; tone: "error" | "warn" | "neutral" }) {
  const color =
    props.value === undefined || props.value === 0
      ? "text-[#4A6480]"
      : props.tone === "error"
        ? "text-severity-error"
        : props.tone === "warn"
          ? "text-severity-warn"
          : "text-[#B6C8DA]";
  return (
    <div>
      <div className="font-mono text-[9.5px] tracking-[0.12em] text-[#5F7590]">{props.caption}</div>
      <div className={`font-mono text-base leading-[1.1] ${color}`}>
        {props.value === undefined ? "—" : props.value.toLocaleString(getFormatLocale())}
      </div>
    </div>
  );
}
